import { formatCurrency, formatQuantity, formatRate, formatPercentage } from '@/utils/formatters';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { 
  ArrowLeft, Factory, Package, Layers, Calendar, 
  User, FileText, Clock, CheckCircle, AlertCircle, Loader2, Edit
} from 'lucide-react';
import EditWODialog from '@/components/EditWODialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyles = {
  created: 'bg-blue-100 text-blue-800',
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  received: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

// Helper to determine WO type and SO references
const getWOTypeInfo = (workOrder) => {
  if (!workOrder) return { type: 'unknown', label: 'Unknown', badge: 'gray' };
  
  const woNumber = workOrder.work_order_number || '';
  const woAllocations = workOrder.wo_allocations || [];
  
  // Extract SO line codes from allocations
  const soLineCodes = [];
  for (const alloc of woAllocations) {
    // For SFG WOs, parent WO number IS the SO line code (e.g., AMBMU0152L-01)
    const parentWO = alloc.work_order_number || alloc.so_number || '';
    if (parentWO && !parentWO.startsWith('WO-') && !parentWO.startsWith('IWO-')) {
      soLineCodes.push(parentWO);
    }
  }
  
  // Direct SO reference takes priority
  if (workOrder.so_number) {
    return {
      type: 'so_direct',
      label: 'SO Production',
      badge: 'green',
      soRef: workOrder.so_number,
      soRefLabel: 'SO Ref'
    };
  }
  
  // Internal WO
  if (woNumber.startsWith('IWO-')) {
    return {
      type: 'internal',
      label: 'IWO',
      badge: 'gray',
      soRef: 'Internal Production',
      soRefLabel: 'Type'
    };
  }
  
  // SFG WO (WO-XXXX format with parent WO allocations)
  if (woNumber.startsWith('WO-')) {
    if (soLineCodes.length === 0) {
      return {
        type: 'sfg',
        label: 'SFG',
        badge: 'purple',
        soRef: 'Stock Production',
        soRefLabel: 'SO Ref'
      };
    }
    
    if (soLineCodes.length <= 3) {
      return {
        type: 'sfg',
        label: 'SFG',
        badge: 'purple',
        soRef: soLineCodes.join(', '),
        soRefLabel: 'SO Ref'
      };
    }
    
    // Multi-SO
    return {
      type: 'multi_so',
      label: 'Multi-SO',
      badge: 'blue',
      soRef: `Multi-SO (${soLineCodes.length})`,
      soRefLabel: 'SO Ref',
      allSoRefs: soLineCodes
    };
  }
  
  // Standard SO-based WO (number derived from SO line code)
  return {
    type: 'so_direct',
    label: 'SO Production',
    badge: 'green',
    soRef: woNumber.replace(/-\d{2}$/, ''),  // Remove WO sequence suffix
    soRefLabel: 'SO Ref'
  };
};

const woTypeBadgeStyles = {
  green: 'bg-green-100 text-green-800 border-green-200',
  purple: 'bg-purple-100 text-purple-800 border-purple-200',
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  gray: 'bg-gray-100 text-gray-800 border-gray-200',
};

export default function WorkOrderDetailPage() {
  const { woId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [workOrder, setWorkOrder] = useState(null);
  const [materialSummary, setMaterialSummary] = useState(null);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  // Bug 4 — SFG WO parent chain (SFG -> parent FNG WO(s) -> SO(s)).
  // Only fetched when workOrder.is_sfg_wo is true.
  const [parentChain, setParentChain] = useState(null);
  const [parentChainLoading, setParentChainLoading] = useState(false);
  const [parentChainError, setParentChainError] = useState(null);

  useEffect(() => {
    if (woId) {
      fetchWorkOrderDetails();
    }
  }, [woId]);

  // Bug 4: fetch the parent-chain for SFG WOs once the WO doc is loaded.
  useEffect(() => {
    if (!workOrder || !workOrder.is_sfg_wo || !woId) {
      setParentChain(null);
      setParentChainError(null);
      return;
    }
    let cancelled = false;
    setParentChainLoading(true);
    setParentChainError(null);
    axios
      .get(`${API_URL}/work-orders/${woId}/parent-chain`, { headers: getAuthHeader() })
      .then((r) => { if (!cancelled) setParentChain(r.data); })
      .catch((e) => {
        if (!cancelled) {
          console.error('parent-chain fetch failed', e);
          setParentChainError(e?.response?.data?.detail || 'Failed to load parent chain');
        }
      })
      .finally(() => { if (!cancelled) setParentChainLoading(false); });
    return () => { cancelled = true; };
  }, [workOrder, woId]);

  const fetchWorkOrderDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch WO details
      const woResponse = await axios.get(`${API_URL}/work-orders/${woId}`, { 
        headers: getAuthHeader() 
      });
      setWorkOrder(woResponse.data);
      
      // Fetch material summary
      try {
        const matResponse = await axios.get(`${API_URL}/work-orders/${woId}/material-summary`, { 
          headers: getAuthHeader() 
        });
        setMaterialSummary(matResponse.data);
      } catch (err) {
        console.log('Material summary not available');
      }

      // Fetch canonical cost breakdown (FIFO material + OJC + BPD →
      // net_production_cost). Single source of truth — replaces the
      // legacy total_material_cost / total_labour_cost reads on the WO doc.
      try {
        const costResponse = await axios.get(`${API_URL}/work-orders/${woId}/actual-production-cost`, {
          headers: getAuthHeader()
        });
        setCostBreakdown(costResponse.data?.cost_breakdown || null);
      } catch (err) {
        console.log('Cost breakdown not available');
      }
      
    } catch (error) {
      console.error('Error fetching work order:', error);
      toast.error('Failed to load work order details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    return `₹${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getProcessStatus = (process) => {
    if (process.completed_quantity >= (workOrder?.quantity || 0)) return 'completed';
    if (process.issued_quantity > 0) return 'in_progress';
    return 'pending';
  };

  const handleGoBack = () => {
    // Always navigate to Sales Orders page as this is the designated origin
    navigate('/production/sales-orders');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!workOrder) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Button variant="outline" onClick={handleGoBack} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Sales Orders
          </Button>
          <Card>
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600">Work Order Not Found</h3>
              <p className="text-sm text-slate-400 mt-1">The requested work order does not exist.</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Get WO type info for display
  const woTypeInfo = getWOTypeInfo(workOrder);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="wo-detail-page">
        {/* Back Button */}
        <Button variant="outline" onClick={handleGoBack} className="mb-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Sales Orders
        </Button>

        {/* WO Header */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Factory className="h-8 w-8 text-indigo-600" />
                <div>
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-2xl">{workOrder.work_order_number}</CardTitle>
                    {/* WO Type Badge - Issue 6 */}
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${woTypeBadgeStyles[woTypeInfo.badge]}`}
                      data-testid="wo-type-badge"
                    >
                      {woTypeInfo.label}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">Work Order Details</p>
                </div>
              </div>
            <div className="flex items-center gap-3">
              <Badge className={`text-sm px-3 py-1 ${statusStyles[workOrder.status] || statusStyles.pending}`}>
                {workOrder.status?.toUpperCase() || 'PENDING'}
              </Badge>
              {/* Edit Button - visible for created/issued WOs */}
              {(workOrder.status === 'created' || workOrder.status === 'issued') && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setEditDialogOpen(true)}
                  data-testid="edit-wo-btn"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Package className="h-4 w-4" />
                  Item
                </div>
                <p className="font-semibold">{workOrder.item_name || workOrder.finished_good_name}</p>
                <p className="text-xs text-muted-foreground">{workOrder.item_code}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Layers className="h-4 w-4" />
                  Quantity
                </div>
                <p className="font-semibold text-lg">{workOrder.quantity} {workOrder.unit || 'NOS'}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  {woTypeInfo.soRefLabel}
                </div>
                {/* Bug 4 followup (ITEM 2): when the parent-chain endpoint has
                    loaded for an SFG WO, use its accurate counts. The legacy
                    getWOTypeInfo.soRef ("Multi-SO (N)") counted parent WO
                    numbers that look like SO line codes — not deduped, and
                    parents whose own number starts with WO-/IWO- were filtered
                    out, so the displayed N was neither parent count nor SO
                    count. The chain endpoint's rollup.distinct_so_count is the
                    true distinct-SO count; parents.length is the true count
                    of contributing parent WOs. */}
                {workOrder.is_sfg_wo && parentChain ? (
                  <>
                    <p className="font-semibold" data-testid="wo-so-reference">
                      {parentChain.parents.length} parent WO
                      {parentChain.parents.length === 1 ? '' : 's'} ·{' '}
                      {parentChain.rollup?.distinct_so_count ?? 0} SO
                      {(parentChain.rollup?.distinct_so_count ?? 0) === 1 ? '' : 's'}
                    </p>
                    {(parentChain.rollup?.distinct_so_count ?? 0) > 3 && (
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer hover:text-blue-600">
                          View all {parentChain.rollup.distinct_so_count} SOs
                        </summary>
                        <ul className="mt-1 space-y-0.5 pl-2">
                          {[...new Map(
                            (parentChain.parents || []).flatMap((p) =>
                              (p.sos || []).map((s) => [s.sales_order_id, s.so_number])
                            )
                          ).values()].filter(Boolean).map((soNum, i) => (
                            <li key={i}>{soNum}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-semibold" data-testid="wo-so-reference">{woTypeInfo.soRef}</p>
                    {woTypeInfo.allSoRefs && woTypeInfo.allSoRefs.length > 3 && (
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer hover:text-blue-600">View all {woTypeInfo.allSoRefs.length} SOs</summary>
                        <ul className="mt-1 space-y-0.5 pl-2">
                          {woTypeInfo.allSoRefs.map((ref, i) => (
                            <li key={i}>{ref}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </>
                )}
                {workOrder.customer_name && <p className="text-xs text-muted-foreground">{workOrder.customer_name}</p>}
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Delivery Date
                </div>
                <p className="font-semibold">{formatDate(workOrder.delivery_date)}</p>
              </div>
            </div>
            
            {/* Additional Info Row */}
            <div className="mt-6 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  Created
                </div>
                <p className="text-sm">{formatDate(workOrder.created_at)}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  Created By
                </div>
                <p className="text-sm">{workOrder.created_by_name || '-'}</p>
              </div>
              {workOrder.is_multi_so && (
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">Linked SOs</div>
                  <p className="text-sm">{workOrder.linked_sos?.join(', ') || '-'}</p>
                </div>
              )}
              {workOrder.nett_status_snapshot && (
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">Nett Status at Creation</div>
                  <p className="text-sm font-medium text-red-600">
                    {workOrder.nett_status_snapshot.nett_status}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bug 4 — SFG WO Serves chain (parent FNG WO(s) -> SO(s)). Renders
            only for SFG WOs; uses the /work-orders/{id}/parent-chain endpoint. */}
        {workOrder.is_sfg_wo && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Layers className="h-5 w-5 text-purple-600" />
                Serves
                {parentChain?.rollup?.distinct_so_count > 0 && (
                  <Badge variant="outline" className="ml-2 text-xs">
                    {parentChain.parents.length} parent WO
                    {parentChain.parents.length === 1 ? '' : 's'} ·{' '}
                    {parentChain.rollup.distinct_so_count} SO
                    {parentChain.rollup.distinct_so_count === 1 ? '' : 's'}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {parentChainLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading parent chain...
                </div>
              )}
              {parentChainError && !parentChainLoading && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {parentChainError}
                </div>
              )}
              {!parentChainLoading && !parentChainError && parentChain && (
                parentChain.parents.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No parent work orders linked to this SFG WO.
                  </div>
                ) : (
                  <>
                    {parentChain.rollup.earliest_so_delivery_date && (
                      <div className="mb-3 text-sm">
                        <span className="text-muted-foreground">Earliest SO delivery:</span>{' '}
                        <span className="font-semibold">
                          {formatDate(parentChain.rollup.earliest_so_delivery_date)}
                        </span>
                        <span className="ml-3 text-muted-foreground">Total allocated:</span>{' '}
                        <span className="font-semibold">{parentChain.rollup.total_allocated_qty}</span>
                      </div>
                    )}
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Parent WO</TableHead>
                          <TableHead>Parent Item</TableHead>
                          <TableHead className="text-right">Allocated Qty</TableHead>
                          <TableHead>Parent Delivery</TableHead>
                          <TableHead>SO Number(s)</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>SO Delivery</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {parentChain.parents.map((p, i) => (
                          <TableRow key={p.parent_wo_id || i}>
                            <TableCell className="font-medium">
                              {p.parent_wo_number || '-'}
                            </TableCell>
                            <TableCell>{p.parent_item_name || '-'}</TableCell>
                            <TableCell className="text-right">{p.allocated_qty}</TableCell>
                            <TableCell>{formatDate(p.allocated_delivery_date)}</TableCell>
                            <TableCell>
                              {(p.sos || []).length === 0
                                ? <span className="text-muted-foreground">-</span>
                                : (p.sos || []).map((s, j) => (
                                    <div key={s.sales_order_id || j} className="text-sm">
                                      {s.line_item_code || s.so_number || s.sales_order_id}
                                    </div>
                                  ))}
                            </TableCell>
                            <TableCell>
                              {(p.sos || []).map((s, j) => (
                                <div key={(s.sales_order_id || j) + '-c'} className="text-sm">
                                  {s.customer_name || '-'}
                                </div>
                              ))}
                            </TableCell>
                            <TableCell>
                              {(p.sos || []).map((s, j) => (
                                <div key={(s.sales_order_id || j) + '-d'} className="text-sm">
                                  {formatDate(s.so_delivery_date)}
                                </div>
                              ))}
                            </TableCell>
                            <TableCell>
                              <Badge className={statusStyles[p.parent_wo_status] || 'bg-gray-100 text-gray-800'}>
                                {p.parent_wo_status || 'unknown'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )
              )}
            </CardContent>
          </Card>
        )}

        {/* Processes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Layers className="h-5 w-5 text-indigo-600" />
              Processes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {workOrder.production_processes && workOrder.production_processes.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Process</TableHead>
                    <TableHead>Job Card</TableHead>
                    <TableHead className="text-right">Issued Qty</TableHead>
                    <TableHead className="text-right">Completed Qty</TableHead>
                    <TableHead className="text-right">Pending</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workOrder.production_processes.map((process, idx) => {
                    const status = getProcessStatus(process);
                    const pendingQty = workOrder.quantity - (process.completed_quantity || 0);
                    
                    return (
                      <TableRow key={process.id || idx}>
                        <TableCell className="font-medium">{idx + 1}</TableCell>
                        <TableCell className="font-medium">{process.process_name}</TableCell>
                        <TableCell>
                          <span className="font-mono text-sm text-muted-foreground">
                            {process.job_card_number || '-'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{process.issued_quantity || 0}</TableCell>
                        <TableCell className="text-right">{process.completed_quantity || 0}</TableCell>
                        <TableCell className="text-right font-medium text-orange-600">
                          {pendingQty > 0 ? pendingQty : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
                            {status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                            {status.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <p className="text-muted-foreground text-center py-6">No processes defined</p>
            )}
          </CardContent>
        </Card>

        {/* Materials (BOM) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-indigo-600" />
              Materials (BOM)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {materialSummary?.materials && materialSummary.materials.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Material</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead className="text-right">BOM Qty</TableHead>
                    <TableHead className="text-right">Issued Qty</TableHead>
                    <TableHead className="text-right">Pending</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materialSummary.materials.map((mat, idx) => {
                    const pending = (mat.bom_qty || 0) - (mat.total_issued || 0);
                    const isComplete = pending <= 0;
                    
                    return (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{mat.item_name}</TableCell>
                        <TableCell className="text-muted-foreground">{mat.item_code}</TableCell>
                        <TableCell className="text-right">{mat.bom_qty || 0} {mat.unit}</TableCell>
                        <TableCell className="text-right">{mat.total_issued || 0} {mat.unit}</TableCell>
                        <TableCell className="text-right font-medium text-orange-600">
                          {pending > 0 ? `${pending} ${mat.unit}` : '-'}
                        </TableCell>
                        <TableCell>
                          {isComplete ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Complete
                            </Badge>
                          ) : (
                            <Badge className="bg-orange-100 text-orange-800">
                              {pending} pending
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : workOrder.bom_materials && workOrder.bom_materials.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Material</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead>Unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workOrder.bom_materials.map((mat, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{mat.item_name}</TableCell>
                      <TableCell className="text-right">{mat.quantity}</TableCell>
                      <TableCell>{mat.unit}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-muted-foreground text-center py-6">No materials information available</p>
            )}
          </CardContent>
        </Card>

        {/* Cost Summary — canonical net_production_cost as primary,
            with material/OJC/BPD breakdown rows beneath for operational
            visibility into the formula components. */}
        <Card data-testid="wo-cost-summary-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              Cost Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primary: Net Production Cost */}
            <div className="p-5 bg-indigo-50 rounded-lg border border-indigo-100">
              <p className="text-sm text-indigo-700 font-medium">Net Production Cost</p>
              <p
                data-testid="wo-net-production-cost"
                className="text-3xl font-bold text-indigo-700 mt-1"
              >
                {formatCurrency(
                  costBreakdown?.net_production_cost ??
                  workOrder.net_production_cost ?? 0
                )}
              </p>
              <p className="text-xs text-indigo-600/70 mt-1">
                = Material + OJC − BPD (FIFO-derived, in-house labour excluded)
              </p>
            </div>

            {/* Secondary: breakdown rows */}
            <div className="mt-4 divide-y divide-slate-100 border border-slate-100 rounded-lg">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-muted-foreground">Material Cost</span>
                <span data-testid="wo-cost-material" className="text-base font-semibold">
                  {formatCurrency(costBreakdown?.material_cost ?? 0)}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-muted-foreground">OJC Cost</span>
                <span data-testid="wo-cost-ojc" className="text-base font-semibold">
                  + {formatCurrency(costBreakdown?.ojc_cost ?? workOrder.total_ojc_cost ?? 0)}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-muted-foreground">BPD Value (deduction)</span>
                <span data-testid="wo-cost-bpd" className="text-base font-semibold text-emerald-700">
                  − {formatCurrency(costBreakdown?.bpd_value ?? workOrder.total_bpd_value ?? 0)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit WO Dialog */}
        <EditWODialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          workOrder={workOrder}
          onSuccess={fetchWorkOrderDetails}
        />
      </div>
    </DashboardLayout>
  );
}
